// Original PC .TER collision data. Layout checked against BrickBench's
// TerrainLoader; coordinates and property bytes are retained without mirroring.
export function parseTerrain(source) {
  if (!(source instanceof ArrayBuffer) || source.byteLength < 8 || source.byteLength > 256 * 1024 * 1024) throw new Error('Invalid terrain file size.');
  const buffer = source.slice(0), view = new DataView(buffer);
  function range(o, n, end = buffer.byteLength) {
    if (!Number.isSafeInteger(o) || !Number.isSafeInteger(n) || o < 0 || n < 0 || o + n > end || end > buffer.byteLength) throw new Error(`Terrain data is out of bounds at ${o}.`);
    return o;
  }
  const i32 = o => view.getInt32(range(o, 4), true), u32 = o => view.getUint32(range(o, 4), true);
  const u16 = o => view.getUint16(range(o, 2), true), i16 = o => view.getInt16(range(o, 2), true);
  const diagnostics = [], largeCoordinates = [];
  function scalar(o) {
    const value = view.getFloat32(range(o, 4), true);
    if (!Number.isFinite(value)) throw new Error('Invalid terrain coordinate.');
    if (Math.abs(value) > 1e7) largeCoordinates.push({ offset: o, value });
    return value;
  }
  const vec = o => [scalar(o), scalar(o + 4), scalar(o + 8)];
  const objectArea = i32(0) * 2;
  if (objectArea < 4) throw new Error('Invalid terrain object area.');
  const objectCount = u16(objectArea); range(objectArea, objectCount * 0x34);
  let dataOffset = 4, faceCount = 0, blockCount = 0;
  const groups = [], infiniteWalls = [];
  for (let i = 0; i < objectCount; i++) {
    const o = objectArea + i * 0x34, dataSize = i32(o + 4) * 2, type = i16(o + 0x14);
    if (dataSize < 4) throw new Error('Invalid terrain group size.');
    range(dataOffset, dataSize, objectArea); const end = dataOffset + dataSize;
    if (type === 2) {
      if (u32(dataOffset) !== 0x12345678) throw new Error('Unsupported infinite-wall marker.');
      range(dataOffset, 8, end); const pointCount = u16(dataOffset + 4); range(dataOffset + 8, pointCount * 12, end);
      const points = Array.from({ length: pointCount }, (_, p) => vec(dataOffset + 8 + p * 12));
      // The serialized Y component is unused for infinite walls. Some owned
      // files contain the finite 2^31 sentinel; preserve it instead of turning
      // it into a height or silently replacing the authored value.
      infiniteWalls.push({ index: i, points, infiniteVertical: true, heightIgnored: true, address: o, dataOffset, dataSize });
    } else if (type === 0 || type === 1) {
      const translation = vec(o + 8), dynamic = type === 1, blocks = [];
      let cursor = dataOffset, terminated = false;
      while (cursor < end) {
        if (view.getInt8(range(cursor, 1, end)) < 0) { terminated = true; break; }
        range(cursor, 20, end); const subCount = i16(cursor + 2);
        if (subCount < 0 || faceCount + subCount > 2000000 || ++blockCount > 1000000) throw new Error('Invalid terrain face/block count.');
        range(cursor + 20, subCount * 100, end);
        const block = { min: [scalar(cursor + 4), scalar(cursor + 12)], max: [scalar(cursor + 8), scalar(cursor + 16)], flags: [view.getUint8(cursor), view.getUint8(cursor + 1)], faces: [] };
        cursor += 20;
        for (let f = 0; f < subCount; f++, cursor += 100) {
          const vertices = [vec(cursor + 24), vec(cursor + 36), vec(cursor + 48), vec(cursor + 60)];
          const normals = [72, 84].map(offset => [0, 4, 8].map(c => view.getFloat32(range(cursor + offset + c, 4), true)));
          const triangleOnly = normals[1][0] === 0 && normals[1][1] === 65536 && normals[1][2] === 0;
          const degenerateTriangles = [[0, 1, 2], [1, 3, 2]].map(indices => {
            const [a, b, c] = indices.map(j => vertices[j]), ab = b.map((n, j) => n - a[j]), ac = c.map((n, j) => n - a[j]);
            return ab[1] * ac[2] - ab[2] * ac[1] === 0 && ab[2] * ac[0] - ab[0] * ac[2] === 0 && ab[0] * ac[1] - ab[1] * ac[0] === 0;
          });
          for (let t = 0; t < (triangleOnly ? 1 : 2); t++) {
            const finiteNormal = normals[t].every(Number.isFinite);
            if (!finiteNormal && !degenerateTriangles[t]) throw new Error('Invalid terrain coordinate: nonfinite normal on a nondegenerate triangle.');
            if (degenerateTriangles[t]) diagnostics.push({ kind: 'degenerate_triangle', faceAddress: cursor, triangleIndex: t, nonFiniteNormal: !finiteNormal });
          }
          block.faces.push({
            min: [scalar(cursor), scalar(cursor + 8), scalar(cursor + 16)],
            max: [scalar(cursor + 4), scalar(cursor + 12), scalar(cursor + 20)],
            vertices, normals, triangleOnly, degenerateTriangles, properties: [view.getUint8(cursor + 96), view.getUint8(cursor + 97)],
            flags: [view.getUint8(cursor + 98), view.getUint8(cursor + 99)], address: cursor,
          });
          faceCount++;
        }
        blocks.push(block);
      }
      if (!terminated) throw new Error('Missing terrain group terminator.');
      groups.push({ index: i, translation, dynamic, platformIndex: i16(o + 0x16), unsupportedTransform: dynamic,
        referenceValue: i32(o + 0x2c), blocks, address: o, dataOffset, dataSize });
    } else throw new Error(`Unsupported terrain object type ${type}.`);
    dataOffset = end;
  }
  if (dataOffset !== objectArea) throw new Error('Terrain content size does not match its groups.');
  return { groups, infiniteWalls, diagnostics, largeCoordinates, summary: { objects: objectCount, groups: groups.length, staticGroups: groups.filter(g => !g.dynamic).length,
    dynamicGroups: groups.filter(g => g.dynamic).length, blocks: blockCount, faces: faceCount, infiniteWalls: infiniteWalls.length } };
}

// Flatten only fixed terrain. Dynamic group positions alone do not describe
// their rotations/animation; resolve the original GSC/DINI references first.
export function flattenStaticTerrain(terrain) {
  const triangles = [], skippedDegenerateTriangles = [];
  for (const group of terrain.groups) {
    if (group.dynamic || group.unsupportedTransform) continue;
    for (const block of group.blocks) for (const face of block.faces) {
      const vertices = face.vertices.map(p => p.map((v, c) => v + group.translation[c]));
      const append = (indices, normal) => triangles.push({ vertices: indices.map(i => vertices[i].slice()), normal: normal.slice(),
        properties: face.properties.slice(), flags: face.flags.slice(), groupIndex: group.index, faceAddress: face.address });
      if (face.degenerateTriangles?.[0]) skippedDegenerateTriangles.push({ groupIndex: group.index, faceAddress: face.address, triangleIndex: 0 });
      else append([0, 1, 2], face.normals[0]);
      if (!face.triangleOnly) {
        if (face.degenerateTriangles?.[1]) skippedDegenerateTriangles.push({ groupIndex: group.index, faceAddress: face.address, triangleIndex: 1 });
        else append([1, 3, 2], face.normals[1]);
      }
    }
  }
  const positions = new Float32Array(triangles.length * 9), normals = new Float32Array(triangles.length * 3);
  const properties = new Uint8Array(triangles.length * 2), flags = new Uint8Array(triangles.length * 2), groupIndices = new Uint32Array(triangles.length);
  for (let i = 0; i < triangles.length; i++) {
    const triangle = triangles[i]; positions.set(triangle.vertices.flat(), i * 9); normals.set(triangle.normal, i * 3);
    properties.set(triangle.properties, i * 2); flags.set(triangle.flags, i * 2); groupIndices[i] = triangle.groupIndex;
  }
  return { positions, normals, properties, flags, groupIndices, triangles, infiniteWalls: terrain.infiniteWalls, skippedDegenerateTriangles, excludedDynamicGroups: terrain.groups.filter(g => g.dynamic || g.unsupportedTransform).map(g => g.index) };
}
